/**
 * Поле ввода ассистента (US-10 FR-1.2): текст на естественном языке и
 * отправка. Enter отправляет, Shift+Enter переносит строку.
 */
import { SendHorizontal } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

/** Высота поля растёт до этого предела, дальше — внутренний скролл. */
const MAX_HEIGHT_PX = 110;

interface ComposerProps {
  disabled: boolean;
  onSend: (text: string) => void;
}

/**
 * Поле ввода реплики.
 *
 * Кнопки прикрепления файла здесь нет: режим «Загрузка» (FR-7) — Волна 3
 * постановки, эндпоинтов на бэке ещё нет. Кнопка, ведущая в 404, хуже её
 * отсутствия; сам режим при этом виден в переключателе как «скоро».
 */
export const Composer = ({ disabled, onSend }: ComposerProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autogrow = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`;
  };

  const submit = () => {
    const text = value.trim();
    if (text === '' || disabled) return;
    onSend(text);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };


  return (
    <div className="shrink-0 border-t border-surface-border bg-surface p-4">
      <div className="flex items-end gap-2 rounded-card border border-surface-border bg-surface py-1.5 pl-3 pr-1.5 focus-within:border-brand-500">
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          disabled={disabled}
          placeholder={t('assistant.placeholder')}
          aria-label={t('assistant.placeholder')}
          data-testid="assistant-input"
          onChange={(event) => {
            setValue(event.target.value);
            autogrow(event.target);
          }}
          onKeyDown={handleKeyDown}
          className="max-h-28 flex-1 resize-none border-none bg-transparent py-2 text-sm outline-none placeholder:text-text-faint disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || value.trim() === ''}
          aria-label={t('assistant.send')}
          data-testid="assistant-send"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-text-inverted transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SendHorizontal className="size-4" aria-hidden="true" />
        </button>
      </div>
      <p className="mt-2 text-center text-[10px] text-text-faint">
        {t('assistant.hint')}
      </p>
    </div>
  );
};
